import React from "react";
import { createInertiaApp, router } from "@inertiajs/react";
import { createRoot, hydrateRoot } from "react-dom/client";
import { Toaster } from "react-hot-toast";
import { LazyMotion } from "motion/react";
import WebsiteLayout from "./Layouts/WebsiteLayout/WebsiteLayout";
import ErrorBoundary from "./Components/ErrorBoundary";
import { registerWebMcpTools } from "./webmcp";
import "./bootstrap";

registerWebMcpTools();
// import "@assets/fonts/fonts.css";

// GA4's own gtag('config', ...) in website.blade.php only fires once, on
// the real initial document load — an Inertia route change swaps content
// via fetch/history.pushState without ever re-running <head>, so without
// this every in-app navigation after the first page would be invisible in
// Analytics. Fires a page_view for every Inertia navigation (real users
// only — SSR has no window/gtag, and this only runs client-side anyway).
router.on("navigate", (event) => {
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    window.gtag("event", "page_view", {
        page_location: window.location.href,
        page_path: window.location.pathname + window.location.search,
        page_title: document.title,
    });
});

// Animation features load as their own chunk after startup (see motionFeatures.js).
const loadMotionFeatures = () => import("./motionFeatures").then((r) => r.default);

createInertiaApp({
    resolve: async (name) => {
        const pages = import.meta.glob("./Pages/**/*.jsx");
        const importPage = pages[`./Pages/${name}.jsx`];
        if (!importPage) throw new Error(`Page not found: ${name}`);
        const module = await importPage();
        const page = module.default;

        // صفحات Auth لها layout خاص (بدون sidebar)
        if (name.startsWith("Dashboard/Auth/")) {
            page.layout = page.layout || ((page) => page);
        } else if (name.startsWith("Dashboard/")) {
            // Loaded on demand: a static import put the whole dashboard shell
            // (and its libraries) in every public page's startup bundle.
            const { default: DashboardLayout } = await import(
                "./Layouts/DashboardLayout/DashboardLayout"
            );
            page.layout =
                page.layout ||
                ((page) => <DashboardLayout>{page}</DashboardLayout>);
        } else {
            page.layout =
                page.layout ||
                ((page) => <WebsiteLayout>{page}</WebsiteLayout>);
        }

        return page;
    },
    setup({ el, App, props }) {
        // Pre-rendered markup (mobile service pages, see PrerenderedFold
        // middleware) is adopted with hydrateRoot so the first paint stays the
        // LCP instead of being thrown away and repainted. The server tree is
        // exactly <App/>, so Toaster gets its own root in that mode. Without
        // pre-rendered markup nothing changes.
        if (el.firstElementChild) {
            // Lets below-the-fold sections wait for the first interaction
            // (utils/afterFirstInteraction.js) on pre-rendered pages.
            window.__WZ_PRERENDERED = true;
            hydrateRoot(
                el,
                <ErrorBoundary>
                    <LazyMotion features={loadMotionFeatures}>
                    <App {...props} />
                </LazyMotion>
                </ErrorBoundary>,
            );
            const toastEl = document.createElement("div");
            document.body.appendChild(toastEl);
            createRoot(toastEl).render(
                <Toaster
                    position="top-center"
                    reverseOrder={false}
                    toastOptions={{
                        duration: 4000,
                        style: {
                            background: "#fff",
                            color: "#333",
                            padding: "16px",
                            borderRadius: "8px",
                            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                        },
                        success: {
                            duration: 4000,
                            iconTheme: {
                                primary: "#10b981",
                                secondary: "#fff",
                            },
                        },
                        error: {
                            duration: 4000,
                            iconTheme: {
                                primary: "#ef4444",
                                secondary: "#fff",
                            },
                        },
                    }}
                />,
            );
            return;
        }
        createRoot(el).render(
            <ErrorBoundary>
                <LazyMotion features={loadMotionFeatures}>
                    <App {...props} />
                </LazyMotion>
                <Toaster
                    position="top-center"
                    reverseOrder={false}
                    toastOptions={{
                        duration: 4000,
                        style: {
                            background: "#fff",
                            color: "#333",
                            padding: "16px",
                            borderRadius: "8px",
                            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                        },
                        success: {
                            duration: 4000,
                            iconTheme: {
                                primary: "#10b981",
                                secondary: "#fff",
                            },
                        },
                        error: {
                            duration: 4000,
                            iconTheme: {
                                primary: "#ef4444",
                                secondary: "#fff",
                            },
                        },
                    }}
                />
            </ErrorBoundary>,
        );
    },
});
